/**
 * TaskGroupsBar — chips collapsibles pour filtrer les tâches par groupe/liste custom.
 * + bouton "Gérer" → dialog CRUD groupes.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FolderKanban, Plus, Trash2, ChevronDown, ChevronRight, Settings } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_COLORS = ["#64748b", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#a855f7"];

export default function TaskGroupsBar({ activeGroupId, onSelectGroup, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [openManage, setOpenManage] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3b82f6");

  const load = async () => {
    try {
      const r = await api.get("/tasks/groups");
      setGroups(r.data.groups || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/tasks/groups", { name: name.trim(), color, order: groups.length });
      setName(""); await load(); onChanged?.();
      toast.success("Groupe créé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce groupe ? Les tâches y seront détachées.")) return;
    try {
      await api.delete(`/tasks/groups/${id}`);
      await load();
      if (activeGroupId === id) onSelectGroup?.(null);
      onChanged?.();
      toast.success("Groupe supprimé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleCollapse = async (id) => {
    try {
      await api.post(`/tasks/groups/${id}/toggle-collapse`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap" data-testid="task-groups-bar">
        <FolderKanban className="w-4 h-4 text-slate-500 shrink-0" />
        <button
          onClick={() => onSelectGroup?.(null)}
          className={`px-3 py-1 rounded-full text-xs border transition ${
            activeGroupId === null ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="group-chip-all"
        >
          Tous les groupes
        </button>
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => onSelectGroup?.(g.id)}
            className={`px-3 py-1 rounded-full text-xs border transition flex items-center gap-1.5 ${
              activeGroupId === g.id ? "text-white border-transparent" : "bg-white border-slate-200 hover:bg-slate-50"
            }`}
            style={activeGroupId === g.id ? { backgroundColor: g.color } : {}}
            data-testid={`group-chip-${g.id}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
            {g.name}
            <Badge variant="outline" className={`text-[10px] ml-1 ${activeGroupId === g.id ? "bg-white/20 text-white border-white/40" : ""}`}>
              {g.task_count}
            </Badge>
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={() => setOpenManage(true)} className="h-7 text-xs" data-testid="groups-manage-btn">
          <Settings className="w-3 h-3 mr-1" />Gérer
        </Button>
      </div>

      <Dialog open={openManage} onOpenChange={setOpenManage}>
        <DialogContent className="max-w-lg" data-testid="groups-manage-dialog">
          <DialogHeader><DialogTitle>Groupes / Listes personnalisés</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Nom du groupe (ex: Sprint 42)" value={name} onChange={e => setName(e.target.value)} data-testid="group-new-name" />
              <div className="flex gap-1 items-center">
                {DEFAULT_COLORS.map(c => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-slate-900" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    data-testid={`group-color-${c}`} />
                ))}
              </div>
              <Button onClick={create} disabled={!name.trim()} data-testid="group-create-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucun groupe. Créez-en un ci-dessus.</p>}
              {groups.map(g => (
                <div key={g.id} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50" data-testid={`group-row-${g.id}`}>
                  <button onClick={() => toggleCollapse(g.id)} data-testid={`group-toggle-${g.id}`}>
                    {g.collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color }} />
                  <span className="flex-1 text-sm">{g.name}</span>
                  <Badge variant="outline" className="text-[10px]">{g.task_count} tâches</Badge>
                  <Button variant="ghost" size="sm" onClick={() => remove(g.id)} className="h-7 px-2 text-red-500" data-testid={`group-del-${g.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
